import Service, { service } from '@ember/service';
import { queryManager } from 'ember-apollo-client';
import { task } from 'ember-concurrency';

import requestAppointmentReminderPrint from 'my-phorest/gql/mutations/request-appointment-reminder-print.graphql';
import requestClientTravelCardPrint from 'my-phorest/gql/mutations/request-client-travel-card-print.graphql';
import requestClientQuoteByClientIdAndDatePrint from 'my-phorest/gql/mutations/request-client-quote-by-client-id-and-date-print.graphql';

export default class ElectronAppPrintingService extends Service {
  @service('browser/window') window;
  @service electronApp;
  @service printingDevices;
  @service router;
  @service session;

  @queryManager apollo;

  @task
  *printAppointmentReminderTask(
    appointmentId,
    { branchId, forceLocalPrinting = false } = {}
  ) {
    yield this.electronApp.verifyHasPrinterToPrintTo();

    if (!forceLocalPrinting) {
      const remoteTargetId = yield this.electronApp.getRemotePrintingTarget();

      if (remoteTargetId) {
        const result = yield this.apollo.mutate(
          {
            mutation: requestAppointmentReminderPrint,
            variables: {
              appointmentId,
              printingDeviceId: remoteTargetId,
            },
          },
          'requestAppointmentReminderPrint'
        );
        this.printingDevices.showPrintRequestSentSuccessNotificationTask.perform(
          remoteTargetId
        );
        return result;
      }
    }

    const accountId = this.session.getAccountIdByBranchId(branchId);
    const path = this.router.urlFor(
      'accounts.account.print.appointment-reminder',
      accountId,
      appointmentId
    );
    return yield this.#printFromPathPromisified(path);
  }

  @task
  *printClientTravelCardTask(
    appointmentId,
    { branchId, forceLocalPrinting = false } = {}
  ) {
    yield this.electronApp.verifyHasPrinterToPrintTo();

    if (!forceLocalPrinting) {
      const remoteTargetId = yield this.electronApp.getRemotePrintingTarget();

      if (remoteTargetId) {
        const result = yield this.apollo.mutate(
          {
            mutation: requestClientTravelCardPrint,
            variables: {
              appointmentId,
              printingDeviceId: remoteTargetId,
            },
          },
          'requestClientTravelCardPrint'
        );
        this.printingDevices.showPrintRequestSentSuccessNotificationTask.perform(
          remoteTargetId
        );
        return result;
      }
    }

    const accountId = this.session.getAccountIdByBranchId(branchId);
    const path = this.router.urlFor(
      'accounts.account.print.appointment-client-travel-card',
      accountId,
      appointmentId
    );
    return yield this.#printFromPathPromisified(path);
  }

  @task
  *printClientQuoteTask(
    clientId,
    date,
    { branchId, forceLocalPrinting = false } = {}
  ) {
    yield this.electronApp.verifyHasPrinterToPrintTo();

    if (!forceLocalPrinting) {
      const remoteTargetId = yield this.electronApp.getRemotePrintingTarget();

      if (remoteTargetId) {
        const result = yield this.apollo.mutate(
          {
            mutation: requestClientQuoteByClientIdAndDatePrint,
            variables: {
              clientId,
              date,
              printingDeviceId: remoteTargetId,
            },
          },
          'requestClientQuoteByClientIdAndDatePrint'
        );
        this.printingDevices.showPrintRequestSentSuccessNotificationTask.perform(
          remoteTargetId
        );
        return result;
      }
    }

    const accountId = this.session.getAccountIdByBranchId(branchId);
    const path = this.router.urlFor(
      'accounts.account.print.client-quote',
      accountId,
      clientId,
      date
    );
    return yield this.#printFromPathPromisified(path);
  }

  async #printFromPathPromisified(path) {
    const { protocol, host } = this.window.location;
    const printURL = new URL(`${protocol}//${host}${path}`);
    printURL.searchParams.append('printView', 'true');

    return new Promise((resolve, reject) => {
      this.electronApp.printReceipt(
        printURL.toString(),
        {
          successCb: resolve,
          failureCb: reject,
        },
        {
          waitForDataLoadedAttr: true,
        }
      );
    });
  }
}
